import React, { useState } from 'react';

import { faCircleNotch, faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CropIcon } from 'lucide-react';

import useAddFact from '@/api/mutations/useCreateFact';
import Button from '@/components/atoms/Button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import SingleSelect from '@/components/atoms/Select';
import { Textarea } from '@/components/atoms/textarea';
import { ClassificationOptions, RelevanceOptions } from '@/constants/filterOptions';
import { BoundingBox, Classification, DocIds, MyOptionType, Relevance } from '@/types';
import 'react-calendar/dist/Calendar.css';

interface FactAdderProps {
  docs: DocIds[];
  handleNewFactCreated: () => void;
  caseId: string;
  docId?: string;
  sourceText?: string;
  highlightAreas?: BoundingBox[];
}

const FactAdder = ({ handleNewFactCreated, docs, docId, caseId, highlightAreas, sourceText = '' }: FactAdderProps) => {
  const [selectedDoc, setSelectedDoc] = useState<MyOptionType>(null);
  const [selectedRelevance, setSelectedRelevance] = useState<MyOptionType>({
    label: 'Yes',
    value: Relevance.YES,
  });
  const [selectedClassification, setSelectedClassification] = useState<MyOptionType>({
    label: 'Primary',
    value: Classification.PRIMARY,
  });
  const [significance, setSignificance] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [factDateText, setFactDateText] = useState<string>('');

  const { isLoading: isLoadingAddNewFact, mutate } = useAddFact({
    caseId: caseId || null,
    docId: docId || selectedDoc?.value || null,
    onSuccess: handleNewFactCreated,
  });

  const handleChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleAddFact = () => {
    if (selectedClassification && selectedRelevance) {
      mutate({
        factDateText,
        classification: selectedClassification.value,
        relevance: selectedRelevance.value,
        sourceText,
        description,
        significance,
        highlightAreas,
      });
    }
  };

  const handleChangeFactDateText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFactDateText(e.target.value);
  };

  const handleChangeSignificance = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSignificance(e.target.value);
  };

  const docsOptions = docs.map((doc) => {
    return { value: doc.id, label: doc.file.name };
  });

  const disabled = (!selectedDoc && !docId) || !description || !factDateText;
  return (
    <>
      {isLoadingAddNewFact ? (
        <div className="flex h-96 flex-col items-center justify-center gap-8">
          <div className="text-xl">Adding Fact...</div>
          <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-brandSecondary text-4xl" />
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-6 py-4">
          <div className="flex items-center gap-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
            <CropIcon className="h-4 w-4 flex-shrink-0" />
            <span>
              Facts can also be created automatically by clicking the crop icon (left) on a document and click and
              dragging to select the area of fact source text.
            </span>
          </div>

          {sourceText && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="sourceText">Source Text</Label>
              <Textarea id="sourceText" value={sourceText} placeholder="Source Text" disabled className="h-36" />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="description">Fact Description</Label>
            <Input
              id="description"
              onChange={handleChangeDescription}
              value={description}
              placeholder="Enter fact description"
            />
          </div>

          {!docId && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="document">Document</Label>
              <SingleSelect
                options={docsOptions}
                selectedOption={selectedDoc}
                onChange={(newValue) => setSelectedDoc(newValue)}
                placeholder="Select a document"
                className="w-full text-sm"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="factDate">Date of Fact</Label>
            <Input
              id="factDate"
              onChange={handleChangeFactDateText}
              value={factDateText}
              placeholder="Enter date of fact"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="significance">
              Significance <i className="text-gray-500">(Optional)</i>
            </Label>
            <Input
              id="significance"
              onChange={handleChangeSignificance}
              value={significance}
              placeholder="Enter significance"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex w-1/2 flex-col gap-2">
              <Label htmlFor="relevance">Relevance</Label>
              <SingleSelect
                options={Object.values(RelevanceOptions)}
                selectedOption={selectedRelevance}
                onChange={(newValue) => setSelectedRelevance(newValue)}
                placeholder="Select relevance"
                className="w-full text-sm"
              />
            </div>

            <div className="flex w-1/2 flex-col gap-2">
              <Label htmlFor="classification">Classification</Label>
              <SingleSelect
                options={Object.values(ClassificationOptions)}
                selectedOption={selectedClassification}
                onChange={(newValue) => setSelectedClassification(newValue)}
                placeholder="Select classification"
                className="w-full text-sm"
              />
            </div>
          </div>

          <div className="mt-2">
            <Button
              onClick={handleAddFact}
              disabled={disabled}
              text="Add"
              type="primary"
              rounded="base"
              icon={<FontAwesomeIcon icon={faCirclePlus} className="mr-2" />}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FactAdder;
